
// client/src/components/Dashboard.jsx
// Modern Dashboard with KPI cards, calendar, embedded lists, and task list.
// Backward-compatible: retains all original data fetching, event/task logic,
// push subscription, modal drill-down, and footer actions.

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { urlBase64ToUint8Array } from '../utils/pushUtils';
import { format, parseISO, isSameDay, addDays, startOfToday } from 'date-fns';
import AppFooter from './AppFooter';
import { FaList, FaPlus, FaPaperPlane, FaBell } from 'react-icons/fa';
import ScheduledPostsList from './media/ScheduledPostsList';
import ScheduledEventsList from './event/ScheduledEventsList';
import DashboardTaskList from './DashboardTaskList';
import { useTranslation } from 'react-i18next';
import './Dashboard.css';

const PUBLIC_VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function Dashboard() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { tenant } = useParams();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

  // Resolve username reliably (works with your current storage pattern)
  function getUsernameSafe() {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null') || {};
      const me = JSON.parse(localStorage.getItem('me') || 'null') || {};
      const s  = JSON.parse(localStorage.getItem('session') || 'null') || {};
      return (
        u.username ||
        s.username ||
        me.username ||
        (window.__ME__ && window.__ME__.username) ||
        localStorage.getItem('username') ||
        null
      );
    } catch { return localStorage.getItem('username') || null; }
  }
  const username = getUsernameSafe();

  const today = React.useMemo(() => startOfToday(), []);
  const currentDay = today;
  const remainingDays = React.useMemo(
    () => Array.from({ length: 6 }, (_, i) => addDays(today, i + 1)),
    [today]
  );

  // PUSH SUBSCRIPTION EFFECT
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready
      .then(swReg => {
        return swReg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY)
        });
      })
      .then(subscription => {
        return axios.post(`${baseUrl}/api/notifications/push/subscribe`, { subscription });
      })
      .catch(err => console.error('Push subscribe failed:', err));
  }, [baseUrl]);

  // OPTIMIZED: Combined data fetching in a single effect
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const todayStr = format(today, 'yyyy-MM-dd');

        const [eventRes, taskRes] = await Promise.all([
          axios.get(`${baseUrl}/api/events/${tenant}/events`),
          axios.get(`${baseUrl}/api/events/${tenant}/tasks`, {
            params: { date: todayStr, username }
          })
        ]);

        if (eventRes.data.success) setEvents(eventRes.data.data || []);
        if (taskRes.data.success) setTasks(taskRes.data.tasks || []);
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    if (!tenant) return;
    if (!username) {
      setError('Failed to load dashboard data');
      setLoading(false);
      return;
    }
    fetchAll();
  }, [tenant, baseUrl, username]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  // Check if user is mentioned in a task
  const isUserMentioned = (task) => {
    const me = (username || '').toLowerCase();
    const created = (task.createdby || task.createdBy || '').toLowerCase();
    const comment = (task.comment || '').toLowerCase();
    return created === me || comment.includes(`@${me}`);
  };

  const isTimelineEvent = (event) => {
    const name = String(event?.name ?? event?.title ?? '').trim().toLowerCase();
    const type = String(event?.type ?? event?.eventtype ?? '').trim().toLowerCase();
    return name === 'timeline' || type === 'timeline';
  };

  const getEventsForDay = (day) => {
    return events.filter((event) => {
      if (isTimelineEvent(event)) return false;
      if (!event?.startdate || !event?.enddate) return false;
      const start = parseISO(event.startdate);
      const end = parseISO(event.enddate);
      if (isNaN(start) || isNaN(end)) return false;
      return day >= start && day <= end;
    });
  };

  const getTasksForDay = (day) => {
    return tasks.filter(task =>
      task.taskdate && isSameDay(new Date(task.taskdate), day) && isUserMentioned(task)
    );
  };

  const handleNewPost = () => {
    navigate(`/${tenant}/post/new`);
  };

  // ── KPI calculations ──
  const todayEvents = getEventsForDay(currentDay);
  const todayTasks = getTasksForDay(currentDay);
  const urgentTasks = tasks.filter(
    t => (t.priority || '').toLowerCase() === 'urgent' || (t.priority || '').toLowerCase() === 'high'
  );

  // ── Upcoming days rows for grid ──
  const upcomingDays = remainingDays;

  if (loading) return <p>{t('dashboard.loadingText')}</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div className="dashboard-modern" style={{ paddingBottom: 80 }}>

      {/* ── Header ── */}
      <div className="dashboard-header">
        <h2>{t('dashboard.title')}</h2>
        <span className="dashboard-header-date">
          {format(currentDay, 'EEEE, MMMM do')}
        </span>
      </div>

      {/* ── KPI Cards ── */}
      <div className="dashboard-kpi-row">
        <div className="dashboard-kpi-card" onClick={() => handleDayClick(currentDay)} style={{ cursor: 'pointer' }}>
          <div className="dashboard-kpi-value">{todayEvents.length}</div>
          <div className="dashboard-kpi-label">{t('dashboard.kpiEvents', 'Events Today')}</div>
        </div>
        <div className="dashboard-kpi-card" onClick={() => handleDayClick(currentDay)} style={{ cursor: 'pointer' }}>
          <div className="dashboard-kpi-value">{todayTasks.length}</div>
          <div className="dashboard-kpi-label">{t('dashboard.kpiTasks', 'Tasks Today')}</div>
        </div>
        <div className="dashboard-kpi-card">
          <div className="dashboard-kpi-value" style={{ color: urgentTasks.length > 0 ? '#dc3545' : undefined }}>
            {urgentTasks.length}
          </div>
          <div className="dashboard-kpi-label">{t('dashboard.kpiUrgent', 'Urgent/High')}</div>
        </div>
      </div>

      {/* ── Alerts bar (only if urgent tasks exist) ── */}
      {urgentTasks.length > 0 && (
        <div className="dashboard-alerts-bar">
          <FaBell className="alert-icon" />
          <span>
            {t('dashboard.urgentAlert', {
              count: urgentTasks.length,
              defaultValue: `You have ${urgentTasks.length} urgent/high priority task(s) requiring attention.`,
            })}
          </span>
        </div>
      )}

      {/* ── Today Card ── */}
      <div className="dashboard-today-card" onClick={() => handleDayClick(currentDay)}>
        <div className="dashboard-today-header">
          <span className="today-day">{t('dashboard.todayLabel', 'Today')}</span>
          <span className="today-date">{format(currentDay, 'MMMM do, yyyy')}</span>
        </div>
        <div className="dashboard-today-items">
          {[...todayEvents, ...todayTasks].length === 0 && (
            <div className="dashboard-today-empty">
              {t('dashboard.noEventsTasksMessage')}
            </div>
          )}
          {[...todayEvents.map(e => ({ ...e, _type: 'event' })),
            ...todayTasks.map(tk => ({ ...tk, _type: 'task' }))]
            .slice(0, 5)
            .map((item, i) => (
              <div key={i} className="dashboard-today-item">
                <span className={`item-dot ${item._type}`} />
                <span>{item.title || item.name}</span>
              </div>
            ))}
          {(todayEvents.length + todayTasks.length) > 5 && (
            <div style={{ textAlign: 'center', padding: '4px 0' }}>
              <small style={{ color: '#ff6800' }}>
                +{todayEvents.length + todayTasks.length - 5} {t('common.more', 'more')}
              </small>
            </div>
          )}
        </div>
      </div>

      {/* ── Scheduled Posts (embedded) ── */}
      <div className="dashboard-section" style={{ marginBottom: 12 }}>
        <div style={{ marginTop: '8px', marginBottom: '8px' }}>
          <ScheduledPostsList
            tenantId={Number(tenant)}
            embedded={true}
          />
        </div>
      </div>

      {/* ── Scheduled Events (embedded) ── */}
      <div className="dashboard-section" style={{ marginBottom: 12 }}>
        <div style={{ marginTop: '8px', marginBottom: '8px' }}>
          <ScheduledEventsList
            tenantId={Number(tenant)}
            embedded={true}
          />
        </div>
      </div>

      {/* ── Task List (embedded, role-aware) ── */}
      <div className="dashboard-section" style={{ marginBottom: 12 }}>
        <div style={{ marginTop: '8px', marginBottom: '8px' }}>
          <DashboardTaskList
            tenantId={Number(tenant)}
            embedded={true}
          />
        </div>
      </div>

      {/* ── Upcoming Days Grid ── */}
      <div className="dashboard-days-grid">
        {upcomingDays.map((day, index) => {
          const eventsToday = getEventsForDay(day);
          const tasksToday = getTasksForDay(day);
          const dotCount = Math.min(8, eventsToday.length + tasksToday.length);

          return (
            <div
              key={index}
              className="dashboard-day-card"
              onClick={() => handleDayClick(day)}
            >
              <div className="day-name">{format(day, 'EEEE')}</div>
              <div className="day-date">{format(day, 'MMM do')}</div>
              <div className="day-dots">
                {[...Array(dotCount)].map((_, i) => (
                  <span
                    key={i}
                    className={`dot ${i < eventsToday.length ? 'event' : 'task'}`}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Modal with event + task list ── */}
      {showModal && (
        <div className="dashboard-modal-overlay">
          <div className="dashboard-modal-content">
            <div className="dashboard-modal-header">
              {t('dashboard.modalDetailsTitle', { date: format(selectedDay, 'MMMM d, yyyy') })}
            </div>
            <div className="dashboard-modal-body">
              {getEventsForDay(selectedDay).map((event) => (
                <div key={`event-${event.id}`} className="dashboard-modal-item">
                  <div>
                    <strong>{event.name}</strong>
                  </div>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => navigate(`/${tenant}/${event.id}/eventDetail`)}
                  >
                    {t('dashboard.viewEventButton')}
                  </button>
                </div>
              ))}
              {getTasksForDay(selectedDay).map((task) => (
                <div key={`task-${task.id}`} className="dashboard-modal-item">
                  <div>
                    <strong>{task.title}</strong>
                    <p>{task.comment}</p>
                  </div>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => {
                      const detailPath = task.eventitemid
                        ? `/${tenant}/${task.eventid}/${task.eventitemid}/eventItemDetail`
                        : `/${tenant}/${task.eventid}/eventDetail`;
                      navigate(detailPath);
                    }}
                  >
                    {t('dashboard.viewTaskButton')}
                  </button>
                </div>
              ))}
              {getEventsForDay(selectedDay).length === 0 && getTasksForDay(selectedDay).length === 0 && (
                <div className="dashboard-modal-empty">
                  {t('dashboard.noEventsTasksMessage')}
                </div>
              )}
            </div>
            <div className="dashboard-modal-footer">
              <button onClick={closeModal} className="btn btn-secondary">
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bottom action bar ── */}
      <AppFooter
        buttons={[
          {
            icon: <FaList />,
            label: t('dashboard.eventListButton'),
            onClick: () => navigate(`/${tenant}/events`),
          },
          {
            id: "new",
            icon: <FaPaperPlane />,
            label: t('dashboard.postButton'),
            onClick: handleNewPost,
            disabled: loading,
          },
          {
            icon: <FaPlus />,
            label: t('dashboard.addEventButton'),
            onClick: () => navigate(`/${tenant}/eventAdd`),
          },
        ]}
      />
    </div>
  );
}

export default Dashboard;
